import { Stack, Typography } from '@mui/material';
import { ConversationParticipantEntity } from '@provision/conversation-types';
import { ColoredAvatar, PlatformUserAvatar } from 'src/components';

function highlightMatch(baseString: string, searchString: string | undefined) {
  if (!baseString) return '';
  if (!searchString) return baseString;

  // Convert both baseString and searchString to lowercase for case-insensitive matching
  const lowerBaseString = baseString.toLowerCase();
  const lowerSearchString = searchString.toLowerCase();

  const index = lowerBaseString.indexOf(lowerSearchString);
  if (index === -1) {
    // Return the base string unchanged if search string is not found
    return baseString;
  }

  const beforeMatch = baseString.substring(0, index);
  const match = baseString.substring(index, index + searchString.length);
  const afterMatch = baseString.substring(index + searchString.length);

  return (
    <>
      {beforeMatch}
      <b>{match}</b>
      {afterMatch}
    </>
  );
}

interface RequesterListItemProps {
  id: string;
  props?: React.HTMLAttributes<HTMLLIElement>;
  firstLine: string;
  secondLine?: string;
  inputValue?: string;
  externalPartnerId?: number;
  participantEntity?: ConversationParticipantEntity;
  entityPartnerId?: number;
  avatarFileKey?: string | null;
}

export const RequesterListItem = ({
  id,
  props,
  firstLine,
  secondLine = '',
  inputValue,
  externalPartnerId,
  participantEntity,
  entityPartnerId,
  avatarFileKey,
}: RequesterListItemProps) => {
  const isPlatformUser =
    participantEntity === ConversationParticipantEntity.PlatformUser;
  return (
    <li {...props} key={id} style={{ lineHeight: 0, padding: '.5rem 1rem' }}>
      <Stack direction="row" alignItems="center">
        {isPlatformUser ? (
          <PlatformUserAvatar
            partnerId={entityPartnerId}
            avatarFileKey={avatarFileKey}
            name={firstLine}
            sx={{ marginRight: '1rem' }}
          />
        ) : (
          <ColoredAvatar name={firstLine} sx={{ marginRight: '1rem' }} />
        )}
        <Stack sx={{ maxWidth: 250 }}>
          {externalPartnerId ? (
            <Typography style={{ wordWrap: 'break-word' }} variant="caption" noWrap>
              {`Workspace: ${externalPartnerId}`}
            </Typography>
          ) : null}
          <Typography style={{ wordWrap: 'break-word' }} variant="body1" noWrap>
            {highlightMatch(firstLine, inputValue || '')}
          </Typography>
          {!secondLine && participantEntity === ConversationParticipantEntity.Client ? (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              No email address on client record
            </Typography>
          ) : (
            <Typography style={{ wordWrap: 'break-word' }} variant="body2">
              {highlightMatch(secondLine, inputValue || '')}
            </Typography>
          )}
        </Stack>
      </Stack>
    </li>
  );
};
